<template>
  <div class="user-management">
    <div class="container-fluid">
      <header class="page-header mb-4">
        <div class="row align-items-center">
          <div class="col-12 col-md-6 mb-3 mb-md-0">
            <h2 class="page-title mb-0">
              <a href="#" @click.prevent="goToDashboard" class="breadcrumb-link">Dashboard</a>
              <span class="breadcrumb-separator">&gt;</span>
              <span class="breadcrumb-current">User Management</span>
            </h2>
          </div>
          <div class="col-12 col-md-6">
            <div class="header-actions d-flex gap-2 justify-content-md-end">              
              <button class="btn btn-primary d-flex align-items-center" @click="openAddUser">
                <el-icon><Plus /></el-icon>&nbsp;<span class="d-none d-sm-inline">Add User</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div v-if="loading" class="loading-state text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-3">Loading users...</p>
      </div>

      <div v-else-if="error" class="alert alert-danger" role="alert">
        {{ error }}
      </div>

      <div v-else class="row g-3 mb-4">
        <div v-for="user in users" :key="user.userId" class="col-12 col-sm-6 col-lg-4 col-xl-3">
          <div class="card user-card h-100">
            <div class="card-body d-flex flex-column">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <div class="d-flex align-items-center gap-2 flex-grow-1">
                  <div class="user-avatar">
                    <el-icon v-if="!user?.isAdmin"><User /></el-icon>
                    <el-icon v-else><UserFilled /></el-icon>
                  </div>
                  <h5 class="card-title user-name mb-0">{{ user.userName }}</h5>
                </div>
                <span :class="['badge', user.disabled ? 'bg-secondary' : 'bg-success']">
                  {{ user.disabled ? 'Disabled' : 'Enabled' }}
                </span>
              </div>

              <div class="mt-auto">
                <div class="btn-group w-100" role="group">
                  <button class="btn btn-outline-primary btn-sm" @click="editUser(user)" title="Change password">
                    <el-icon><Edit /></el-icon>
                  </button>
                  <button 
                    class="btn btn-outline-warning btn-sm" 
                    @click="toggleAdminStatus(user)" 
                    :title="user.isAdmin ? 'Remove Admin' : 'Set as Admin'"
                  >
                    <el-icon v-if="!user.isAdmin"><Avatar /></el-icon>
                    <el-icon v-else><UserFilled /></el-icon>
                  </button>
                  <button 
                    class="btn btn-outline-secondary btn-sm" 
                    @click="toggleUserStatus(user)" 
                    :title="!user.disabled ? 'Disable' : 'Enable'"
                  >
                    <el-icon v-if="!user.disabled"><SwitchButton /></el-icon>
                    <el-icon v-else><Open /></el-icon>
                  </button>
                  <button class="btn btn-outline-danger btn-sm" @click="deleteUser(user)" title="Delete">
                    <el-icon><Delete /></el-icon>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <nav v-if="!loading && users.length > 0 && !error" aria-label="User pagination">
        <ul class="pagination justify-content-center">
          <li class="page-item" :class="{ disabled: currentPage === 1 }">
            <button class="page-link" @click="onPreviousPage" :disabled="currentPage === 1">
              <el-icon><ArrowLeft /></el-icon>&nbsp;<span class="d-none d-sm-inline">Previous</span>
            </button>
          </li>
          <li class="page-item disabled">
            <span class="page-link">Page {{ currentPage }} of {{ totalPages }}</span>
          </li>
          <li class="page-item" :class="{ disabled: currentPage === totalPages }">
            <button class="page-link" @click="onNextPage" :disabled="currentPage === totalPages">
              <span class="d-none d-sm-inline">Next</span>&nbsp;<el-icon><ArrowRight /></el-icon>
            </button>
          </li>
        </ul>
      </nav>

      <div v-if="!loading && users.length === 0 && !error" class="alert alert-info text-center" role="alert">
        No users found
      </div>

      <!-- Add User Dialog -->
      <el-dialog
        v-model="showAddUserDialog"
        title="Add New User"
        width="450px"
        :close-on-click-modal="false"
      >
        <el-form :model="addUserForm" :rules="addUserRules" ref="addUserFormRef" label-width="140px">
          <el-form-item label="Username" prop="username">
            <el-input
              v-model="addUserForm.username"
              placeholder="Enter username"
              clearable
            />
          </el-form-item>
          <el-form-item label="Password" prop="password">
            <el-input
              v-model="addUserForm.password"
              type="password"
              placeholder="Enter password"
              show-password
            />
          </el-form-item>
          <el-form-item label="Confirm" prop="confirmPassword">
            <el-input
              v-model="addUserForm.confirmPassword"
              type="password"
              placeholder="Re-enter password"
              show-password
            />
          </el-form-item>
          <el-form-item label="Is Admin" prop="isAdmin">
            <el-switch v-model="addUserForm.isAdmin" />
          </el-form-item>
          <el-form-item label="Status" prop="disabled">
            <el-switch 
              v-model="userActived" 
              active-text="Enabled" 
              inactive-text="Disabled"
            />
          </el-form-item>
        </el-form>
        <template #footer>
          <div class="dialog-footer">
            <el-button @click="showAddUserDialog = false">Cancel</el-button>
            <el-button type="primary" @click="handleAddUser" :loading="addingUser">
              Add User
            </el-button>
          </div>
        </template>
      </el-dialog>

      <!-- Change Password Dialog -->
      <el-dialog
        v-model="showChangePasswordDialog"
        title="Change Password"
        width="450px"
        :close-on-click-modal="false"
      >
        <el-form :model="changePasswordForm" :rules="changePasswordRules" ref="changePasswordFormRef" label-width="140px">
          <el-form-item label="New Password" prop="newPassword">
            <el-input
              v-model="changePasswordForm.newPassword"
              type="password"
              placeholder="Enter new password"
              show-password
            />
          </el-form-item>
          <el-form-item label="Confirm" prop="confirmPassword">
            <el-input
              v-model="changePasswordForm.confirmPassword"
              type="password"
              placeholder="Re-enter new password"
              show-password
            />
          </el-form-item>
        </el-form>
        <template #footer>
          <div class="dialog-footer">
            <el-button @click="showChangePasswordDialog = false">Cancel</el-button>
            <el-button type="primary" @click="handleChangePassword" :loading="changingPassword">
              Confirm
            </el-button>
          </div>
        </template>
      </el-dialog>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, getCurrentInstance } from 'vue';
import apiClient from '@/apiClient';
import { useRouter } from 'vue-router';

const router = useRouter();

// state
const users = ref([]);
const userCount = ref(0);
const loading = ref(true);
const error = ref('');
const currentPage = ref(1);
const pageSize = ref(8);
const showAddUserDialog = ref(false);
const addingUser = ref(false);
const addUserFormRef = ref(null);
const showChangePasswordDialog = ref(false);
const changingPassword = ref(false);
const changePasswordFormRef = ref(null);
const selectedUserId = ref(null);
const { proxy } = getCurrentInstance();

// Add user form
const addUserForm = ref({
  username: '',
  password: '',
  confirmPassword: '',
  isAdmin: false,
  disabled: false
});

// Change password form
const changePasswordForm = ref({
  newPassword: '',
  confirmPassword: ''
});

// Validate confirm password
const validateConfirmPassword = (rule, value, callback) => {
  if (value !== addUserForm.value.password) {
    callback(new Error('Passwords do not match'));
  } else {
    callback();
  }
};

// Validate confirm password for change password
const validateChangeConfirmPassword = (rule, value, callback) => {
  if (value !== changePasswordForm.value.newPassword) {
    callback(new Error('Passwords do not match'));
  } else {
    callback();
  }
};

// Add user validation rules
const addUserRules = {
  username: [
    { required: true, message: 'Please enter username', trigger: 'blur' },
    { min: 3, message: 'Username must be at least 3 characters', trigger: 'blur' }
  ],
  password: [
    { required: true, message: 'Please enter password', trigger: 'blur' },
    { min: 6, message: 'Password must be at least 6 characters', trigger: 'blur' }
  ],
  confirmPassword: [
    { required: true, message: 'Please confirm password', trigger: 'blur' },
    { validator: validateConfirmPassword, trigger: 'blur' }
  ]
};

// Change password validation rules
const changePasswordRules = {
  newPassword: [
    { required: true, message: 'Please enter new password', trigger: 'blur' },
    { min: 6, message: 'Password must be at least 6 characters', trigger: 'blur' }
  ],
  confirmPassword: [
    { required: true, message: 'Please confirm new password', trigger: 'blur' },
    { validator: validateChangeConfirmPassword, trigger: 'blur' }
  ]
};


const userActived = computed({
  get: () => !addUserForm.value.disabled,
  set: (val) => {
    addUserForm.value.disabled = !val;
  }
});

const totalPages = computed(() => Math.ceil(userCount.value / pageSize.value));

// methods
function goToDashboard() {
  router.push({ name: 'AdminDashboard' }).catch(() => {});
}

async function loadUsers() {
  loading.value = true;
  error.value = '';
  try {
    const response = await apiClient.post('/Admin/getPaginatedUsers', {
      pageIndex: currentPage.value,
      pageSize: pageSize.value,      
    });
    if (response && response.data) {
      users.value = response.data.users;
      userCount.value = response.data.count || 0;      
    }
  } catch (e) {
    error.value = (e?.response?.data?.msg) || 'Failed to load users';        
  } finally {
    loading.value = false;
  }
}

function openAddUser() {
  showAddUserDialog.value = true;
  // Reset form
  addUserForm.value = {
    username: '',
    password: '',
    confirmPassword: '',
    isAdmin: false,
    disabled: false
  };
  if (addUserFormRef.value) {
    addUserFormRef.value.clearValidate();
  }
}

// Handle add user
async function handleAddUser() {
  if (!addUserFormRef.value) return;
  
  await addUserFormRef.value.validate(async (valid) => {
    if (!valid) return;
    
    addingUser.value = true;
    try {
      const response = await apiClient.post('/Admin/addNewAccount', {
        accountName: addUserForm.value.username,
        password: addUserForm.value.password,
        isAdmin: addUserForm.value.isAdmin,
        disabled: addUserForm.value.disabled
      });
      
      if(response && response.data) {              
        proxy.$handleTips(response.data.tag, response.data.msg, error);
      }       

      showAddUserDialog.value = false;
      
      // Reset form
      addUserForm.value = {
        username: '',
        password: '',
        confirmPassword: '',
        isAdmin: false,
        disabled: true
      };
      
      // Reload users list
      await loadUsers();

    } catch (e) {
      const errorMsg = e?.response?.data?.msg || 'Failed to add user';
      proxy.$handleTips(e?.response?.data?.tag, errorMsg, e);
      
    } finally {
      addingUser.value = false;
    }
  });
}

function editUser(user) {
  selectedUserId.value = user.userId;
  showChangePasswordDialog.value = true;
  // Reset form
  changePasswordForm.value = {
    newPassword: '',
    confirmPassword: ''
  };
  if (changePasswordFormRef.value) {
    changePasswordFormRef.value.clearValidate();
  }
}

// Handle change password
async function handleChangePassword() {
  if (!changePasswordFormRef.value) return;
  
  await changePasswordFormRef.value.validate(async (valid) => {
    if (!valid) return;
    
    changingPassword.value = true;
    try {
      const response = await apiClient.post('/Admin/changeAccountPassword', {
        accountId: selectedUserId.value,
        oldPassword: '',
        newPassword: changePasswordForm.value.newPassword
      });
      
      if(response && response.data) {              
        proxy.$handleTips(response.data.tag, response.data.msg, error);
      }
      
      showChangePasswordDialog.value = false;
      
      // Reset form
      changePasswordForm.value = {
        newPassword: '',
        confirmPassword: ''
      };
      selectedUserId.value = null;
      
    } catch (e) {
      const errorMsg = e?.response?.data?.msg || 'Failed to change password';
      proxy.$handleTips(e?.response?.data?.tag, errorMsg, e);
    } finally {
      changingPassword.value = false;
    }
  });
}

async function toggleUserStatus(user) {
  if(!confirm(`Are you sure you want to ${user.disabled ? 'enable' : 'disable'} user "${user.userName}"?`)) return;
  try {
    const response = await apiClient.post('/Admin/switchAccountStatus', { 
      accountId: user.userId,
      disable: !user.disabled
    });

    if(response && response.data) {              
      proxy.$handleTips(response.data.tag, response.data.msg, error);
      user.disabled = !user.disabled;
    }

    // Reload users list
    await loadUsers();
    
  } catch (e) {
    const errorMsg = e?.response?.data?.msg || 'Failed to update user status';
      proxy.$handleTips(e?.response?.data?.tag, errorMsg, e);
  }
}

async function toggleAdminStatus(user) {
  const action = user.isAdmin ? 'remove admin privileges from' : 'set as admin';
  if(!confirm(`Are you sure you want to ${action} user "${user.userName}"?`)) return;
  try {
    const response = await apiClient.post('/Admin/switchAccountAdmin', { 
      accountId: user.userId,
      isAdmin: !user.isAdmin
    });

    if(response && response.data) {              
      proxy.$handleTips(response.data.tag, response.data.msg, error);
      user.isAdmin = !user.isAdmin;
    }

    // Reload users list
    await loadUsers();
    
  } catch (e) {
    const errorMsg = e?.response?.data?.msg || 'Failed to update admin status';
    proxy.$handleTips(e?.response?.data?.tag, errorMsg, e);
  }
}

async function deleteUser(user) {
  if (!confirm(`Are you sure you want to delete user "${user.userName}"?`)) return;
  try {
    const response = await apiClient.post(`/Admin/removeAccount`,{
      removingAccountId: user.userId
    });

    if(response && response.data) {              
      proxy.$handleTips(response.data.tag, response.data.msg, error);
    }

    // Reload users list
    await loadUsers();

  } catch (e) {
    const errorMsg = e?.response?.data?.msg || 'Failed to delete user';
      proxy.$handleTips(e?.response?.data?.tag, errorMsg, e);
  }
}

async function onPreviousPage() {
  currentPage.value--;
  if(currentPage.value < 1) currentPage.value = 1;
  await loadUsers();
}


async function onNextPage() {
  currentPage.value++;
  await loadUsers();
}


onMounted(() => {
  loadUsers();
});
</script>

<style scoped>
.user-management {
  padding: 24px 0;
  color: #e6f0f2;
  min-height: calc(100vh - 84px);
  background: linear-gradient(180deg, #071018 0%, rgba(7,16,24,0.6) 100%);
}

/* header */
.page-title {
  color: #13c2c2;
  font-weight: 700;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.breadcrumb-link {
  color: #13c2c2;
  text-decoration: none;
  transition: color 0.2s ease;
}

.breadcrumb-link:hover {
  color: #0fa6a6;
  text-decoration: underline;
}

.breadcrumb-separator {
  color: rgba(255,255,255,0.4);
  font-weight: 400;
  margin: 0 4px;
}

.breadcrumb-current {
  color: #fff;
  font-weight: 700;
}

.search-input {
  max-width: 300px;
  background: rgba(0,0,0,0.25);
  border: 1px solid rgba(255,255,255,0.1);
  color: #fff;
}

.search-input::placeholder {
  color: rgba(255,255,255,0.5);
}

.search-input:focus {
  background: rgba(0,0,0,0.35);
  border-color: #13c2c2;
  color: #fff;
  box-shadow: 0 0 0 0.25rem rgba(19,194,194,0.15);
}

/* user cards - matching dashboard stat card style */
.user-card {
  background: linear-gradient(180deg, rgba(19,194,194,0.15), rgba(5,166,166,0.08));
  border: 1px solid rgba(19,194,194,0.25);
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(2,8,12,0.45);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.user-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 14px 40px rgba(2,8,12,0.6);
}

.user-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(255,255,255,0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: #13c2c2;
  flex-shrink: 0;
}

.user-name {
  color: #fff;
  font-size: 1rem;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-email {
  font-size: 0.875rem;
}

.user-meta {
  font-size: 0.8rem;
}

/* Bootstrap button overrides for theme */
.btn-primary {
  background: #13c2c2;
  border-color: #13c2c2;
  color: #081014;
  font-weight: 600;
}

.btn-primary:hover,
.btn-primary:focus {
  background: #0fa6a6;
  border-color: #0fa6a6;
  color: #081014;
}

.btn-outline-primary {
  color: #13c2c2;
  border-color: rgba(19,194,194,0.3);
}

.btn-outline-primary:hover {
  background: rgba(19,194,194,0.15);
  border-color: #13c2c2;
  color: #13c2c2;
}

.btn-outline-warning {
  color: #ffc107;
  border-color: rgba(255,193,7,0.3);
}

.btn-outline-warning:hover {
  background: rgba(255,193,7,0.15);
  border-color: #ffc107;
  color: #ffc107;
}

.btn-outline-secondary {
  color: rgba(255,255,255,0.7);
  border-color: rgba(255,255,255,0.2);
}

.btn-outline-secondary:hover {
  background: rgba(255,255,255,0.1);
  border-color: rgba(255,255,255,0.3);
  color: #fff;
}

.btn-outline-danger {
  color: #ff8b8b;
  border-color: rgba(255,139,139,0.3);
}

.btn-outline-danger:hover {
  background: rgba(255,139,139,0.15);
  border-color: #ff8b8b;
  color: #ff6b6b;
}

/* Pagination */
.pagination .page-link {
  background: rgba(255,255,255,0.04);
  border-color: rgba(255,255,255,0.1);
  color: #bfeff0;
}

.pagination .page-link:hover {
  background: rgba(255,255,255,0.08);
  border-color: rgba(255,255,255,0.15);
  color: #fff;
}

.pagination .page-item.disabled .page-link {
  background: rgba(255,255,255,0.02);
  border-color: rgba(255,255,255,0.05);
  color: rgba(255,255,255,0.4);
}

/* loading spinner */
.spinner-border.text-primary {
  color: #13c2c2 !important;
}

.loading-state {
  color: rgba(255,255,255,0.6);
}

/* alerts */
.alert-danger {
  background: rgba(255,139,139,0.15);
  border-color: rgba(255,139,139,0.3);
  color: #ff8b8b;
}

.alert-info {
  background: rgba(19,194,194,0.12);
  border-color: rgba(19,194,194,0.25);
  color: #bfeff0;
}

/* responsive adjustments */
@media (max-width: 576px) {
  .page-title {
    font-size: 1.25rem;
    gap: 6px;
  }
  
  .breadcrumb-separator {
    margin: 0 2px;
  }
  
  .search-input {
    max-width: 100%;
  }
  
  .header-actions {
    flex-direction: column;
  }
  
  .header-actions .btn {
    width: 100%;
    justify-content: center;
  }
}
</style>